/**
 * Module registry configuration (INF-616 — plane-rooted route trees).
 *
 * ROUTE_PATHS now includes plane prefixes:
 *   - Platform-plane paths are prefixed with /platform
 *   - Partner-plane paths are prefixed with /partner
 *
 * Old unprefixed paths (e.g. ROUTE_PATHS.dashboard === '/dashboard') are
 * kept as backwards-compatibility aliases until R2.3 cutover. Use the
 * prefixed versions for all new work.
 */

import AdminModule, { AdminRoutePaths } from '@modules/admin/module';
import MakerCheckerModule, { MakerCheckerRoutePaths } from '@modules/maker-checker/module';
import AuditEngineModule, { AuditEngineRoutePaths } from '@modules/audit-engine/module';
import BatchProcessingModule, { BatchProcessingRoutePaths } from '@modules/batch-processing/module';
import CardsModule, { CardsRoutePaths } from '@modules/cards/module';
import FinanceModule, { FinanceRoutePaths } from '@modules/finance/module';
import FraudModule, { FraudRoutePaths } from '@modules/fraud/module';
import HelpCenterModule, { HelpCenterRoutePaths } from '@modules/help-center/module';
import HomeModule, { HomeRoutePaths } from '@modules/home/module';
import MovementsModule, { MovementsRoutePaths } from '@modules/movements/module';
import OpsHubModule, { OpsHubRoutePaths } from '@modules/ops-hub/module';
import PartnerChatModule, { PartnerChatRoutePaths } from '@modules/partner-chat/module';
import PartnersPlatformModule, { PartnersPlatformRoutePaths } from '@modules/partners-platform/module';
import TreasuryModule, { TreasuryRoutePaths } from '@modules/treasury/module';
import UsersModule, { UsersRoutePaths } from '@modules/users/module';
import YieldSchedulerModule, { YieldSchedulerRoutePaths } from '@modules/yield-scheduler/module';
import PublicModule, { PublicRoutePaths } from '@public/module';

import { PARTNER_PREFIX, PLATFORM_PREFIX } from '@bootstrap/router/plane';

const loadModuleConfigs = () => {
  const moduleConfigs = {
    home: HomeModule,
    public: PublicModule,
    users: UsersModule,
    fraud: FraudModule,
    movements: MovementsModule,
    finance: FinanceModule,
    treasury: TreasuryModule,
    admin: AdminModule,
    batchProcessing: BatchProcessingModule,
    auditEngine: AuditEngineModule,
    makerChecker: MakerCheckerModule,
    opsHub: OpsHubModule,
    cards: CardsModule,
    partnerChat: PartnerChatModule,
    partnersPlatform: PartnersPlatformModule,
    yieldScheduler: YieldSchedulerModule,
    helpCenter: HelpCenterModule,
  };
  return moduleConfigs;
};

/** Prefix every path in a route-paths object with a plane prefix. */
function prefixPaths<T extends Record<string, string>>(paths: T, prefix: string): T {
  return Object.fromEntries(
    Object.entries(paths).map(([key, path]) => [key, `${prefix}${path}`])
  ) as T;
}

export const ROUTE_PATHS = {
  root: '/',
  // Public / auth paths — no plane prefix
  ...PublicRoutePaths,
  // Platform-plane paths
  ...prefixPaths(HomeRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(UsersRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(FraudRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(MovementsRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(FinanceRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(TreasuryRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(AdminRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(BatchProcessingRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(AuditEngineRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(MakerCheckerRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(OpsHubRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(CardsRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(PartnersPlatformRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(YieldSchedulerRoutePaths, PLATFORM_PREFIX),
  ...prefixPaths(HelpCenterRoutePaths, PLATFORM_PREFIX),
  // Partner-plane paths
  ...prefixPaths(PartnerChatRoutePaths, PARTNER_PREFIX),
};

export default loadModuleConfigs;
